import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Mail, Link2, Unplug, RefreshCw, Plus, X, Save, CheckCircle2, Zap, ZapOff } from "lucide-react";

/**
 * MicrosoftConnect — UI for end-users to connect their own Outlook mailbox
 * via OAuth and configure how the AI should filter incoming mails.
 */
export default function MicrosoftConnect({ lang = "de" }) {
    const [status, setStatus] = useState(null);
    const [rules, setRules] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [keepInput, setKeepInput] = useState("");
    const [dropInput, setDropInput] = useState("");
    const [syncing, setSyncing] = useState(false);

    const t = (de, en) => (lang === "de" ? de : en);

    const load = useCallback(async () => {
        try {
            const [s, r, sub] = await Promise.all([
                api.get("/ms/connection"),
                api.get("/ms/personal-rules"),
                api.get("/ms/subscriptions").catch(() => ({ data: [] })),
            ]);
            setStatus(s.data);
            setRules(r.data);
            setSubscriptions(sub.data || []);
        } catch (e) {
            toast.error(errMsg(e, "Fehler"));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Listen for ?ms_connected=1 query param after OAuth callback redirect.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("ms_connected") === "1") {
            toast.success(t("Microsoft erfolgreich verbunden", "Microsoft connected successfully"));
            window.history.replaceState({}, "", window.location.pathname);
            load();
        }
        const err = params.get("ms_error");
        if (err) {
            toast.error(`Microsoft: ${err}`);
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const connect = async () => {
        try {
            const { data } = await api.get("/ms/auth/login");
            window.location.href = data.authorize_url;
        } catch (e) { toast.error(errMsg(e)); }
    };

    const disconnect = async () => {
        if (!window.confirm(t("Verbindung wirklich trennen?", "Really disconnect?"))) return;
        try {
            await api.delete("/ms/connection");
            toast.success(t("Getrennt", "Disconnected"));
            await load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const sync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/ms/sync-personal");
            if (data.ok) toast.success(`${data.count || 0} ${t("Mails verarbeitet", "mails processed")}`);
            else toast.error(data.error || "Fehler");
        } catch (e) { toast.error(errMsg(e)); }
        finally { setSyncing(false); }
    };

    const personalSub = subscriptions.find((s) => s.mailbox_id === "me");
    const realtimeEnabled = !!personalSub;
    const toggleRealtime = async () => {
        try {
            if (realtimeEnabled) {
                await api.delete(`/ms/subscriptions/${personalSub.subscription_id}`);
                toast.success(t("Echtzeit deaktiviert", "Real-time disabled"));
            } else {
                await api.post("/ms/subscriptions", { mailbox: "me" });
                toast.success(t("Echtzeit aktiviert — Mails werden in Sekundenschnelle verarbeitet", "Real-time enabled — mails processed in seconds"));
            }
            await load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const saveRules = async () => {
        try {
            await api.put("/ms/personal-rules", rules);
            toast.success(t("Regeln gespeichert", "Rules saved"));
        } catch (e) { toast.error(errMsg(e)); }
    };

    const setRule = (i, field, value) => {
        setRules((r) => {
            const nr = { ...r, rules: [...r.rules] };
            nr.rules[i] = { ...nr.rules[i], [field]: value };
            return nr;
        });
    };

    const addKeyword = (key, val) => {
        if (!val.trim()) return;
        setRules((r) => ({ ...r, [key]: [...(r[key] || []), val.trim()] }));
    };
    const removeKeyword = (key, idx) => {
        setRules((r) => ({ ...r, [key]: (r[key] || []).filter((_, i) => i !== idx) }));
    };

    return (
        <section className="p-5 bg-white border border-neutral-200 rounded-lg mb-4" data-testid="ms-connect-section">
            <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-blue-600" /> {t("Microsoft 365 / Outlook", "Microsoft 365 / Outlook")}
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
                {t("Verbinde dein Outlook-Postfach. Die KI filtert eingehende Mails und legt nur die wichtigen als Task an — Werbung wird ignoriert.",
                   "Connect your Outlook mailbox. The AI filters incoming mail and only creates tasks for important ones — ads are ignored.")}
            </p>

            {/* Connection status */}
            {status?.connected ? (
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-start gap-2 mb-4" data-testid="ms-connected-status">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{status.info?.ms_display_name}</p>
                        <p className="text-xs text-neutral-600 truncate">{status.info?.ms_user_upn}</p>
                        <p className="text-[10px] text-neutral-500">{t("Verbunden seit", "Connected since")} {new Date(status.info?.connected_at).toLocaleString(lang === "de" ? "de-DE" : "en-US")}</p>
                    </div>
                    <Button size="sm" onClick={sync} variant="outline" disabled={syncing} data-testid="ms-sync-btn">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />
                        {t("Jetzt synchronisieren", "Sync now")}
                    </Button>
                    <Button size="sm" onClick={toggleRealtime} variant="outline" className={realtimeEnabled ? "border-emerald-300 bg-emerald-50 text-emerald-700" : ""} data-testid="ms-realtime-toggle">
                        {realtimeEnabled ? <Zap className="w-3.5 h-3.5 mr-1 text-emerald-600" /> : <ZapOff className="w-3.5 h-3.5 mr-1 text-neutral-400" />}
                        {realtimeEnabled ? t("Echtzeit AN", "Real-time ON") : t("Echtzeit AUS", "Real-time OFF")}
                    </Button>
                    <Button size="sm" onClick={disconnect} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="ms-disconnect-btn">
                        <Unplug className="w-3.5 h-3.5 mr-1" /> {t("Trennen", "Disconnect")}
                    </Button>
                </div>
            ) : (
                <Button onClick={connect} className="bg-blue-600 hover:bg-blue-700 text-white mb-4" data-testid="ms-connect-btn">
                    <Link2 className="w-4 h-4 mr-2" /> {t("Mit Microsoft verbinden", "Connect Microsoft")}
                </Button>
            )}

            {/* Rules editor */}
            {status?.connected && rules && (
                <div className="border-t border-neutral-100 pt-4">
                    <h3 className="font-semibold text-sm mb-2">{t("KI-Regeln für mein Postfach", "AI rules for my mailbox")}</h3>

                    {/* Keywords */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="text-xs font-semibold text-neutral-700">{t("Begriffe, die mich INTERESSIEREN (immer Task)", "Keep keywords (force as task)")}</label>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {(rules.keywords_keep || []).map((k, i) => (
                                    <span key={i} className="bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded flex items-center gap-1" data-testid={`ms-keep-kw-${i}`}>
                                        {k} <button onClick={() => removeKeyword("keywords_keep", i)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-1 mt-1.5">
                                <input value={keepInput} onChange={(e) => setKeepInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { addKeyword("keywords_keep", keepInput); setKeepInput(""); } }} placeholder={t("z.B. Vertrag, Angebot", "e.g. Contract, Offer")} className="flex-1 text-xs h-7 border border-neutral-200 rounded px-2" data-testid="ms-keep-input" />
                                <Button size="sm" variant="outline" onClick={() => { addKeyword("keywords_keep", keepInput); setKeepInput(""); }} className="h-7"><Plus className="w-3 h-3" /></Button>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-neutral-700">{t("Begriffe, die ich NICHT will (sofort ignorieren)", "Drop keywords (always ignore)")}</label>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {(rules.keywords_drop || []).map((k, i) => (
                                    <span key={i} className="bg-red-50 text-red-700 text-xs px-2 py-0.5 rounded flex items-center gap-1" data-testid={`ms-drop-kw-${i}`}>
                                        {k} <button onClick={() => removeKeyword("keywords_drop", i)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-1 mt-1.5">
                                <input value={dropInput} onChange={(e) => setDropInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { addKeyword("keywords_drop", dropInput); setDropInput(""); } }} placeholder={t("z.B. Newsletter, Promo", "e.g. Newsletter, Promo")} className="flex-1 text-xs h-7 border border-neutral-200 rounded px-2" data-testid="ms-drop-input" />
                                <Button size="sm" variant="outline" onClick={() => { addKeyword("keywords_drop", dropInput); setDropInput(""); }} className="h-7"><Plus className="w-3 h-3" /></Button>
                            </div>
                        </div>
                    </div>

                    {/* Per-category rules */}
                    <div className="space-y-1.5 mb-3">
                        {(rules.rules || []).map((r, i) => (
                            <div key={r.category} className="grid grid-cols-12 gap-2 items-center border border-neutral-100 rounded px-2 py-1.5" data-testid={`ms-rule-${r.category}`}>
                                <div className="col-span-3 text-xs font-semibold capitalize">{CAT_LABEL[r.category]?.[lang === "de" ? "de" : "en"] || r.category}</div>
                                <div className="col-span-2">
                                    <input type="checkbox" checked={!!r.enabled} onChange={(e) => setRule(i, "enabled", e.target.checked)} data-testid={`ms-rule-enabled-${r.category}`} />
                                </div>
                                <div className="col-span-3">
                                    <select value={r.action} onChange={(e) => setRule(i, "action", e.target.value)} className="w-full text-xs h-7 border border-neutral-200 rounded px-1" data-testid={`ms-rule-action-${r.category}`}>
                                        <option value="task">{t("→ Task erstellen", "→ Create task")}</option>
                                        <option value="ignore">{t("→ Ignorieren", "→ Ignore")}</option>
                                        <option value="move_folder">{t("→ In Ordner verschieben", "→ Move to folder")}</option>
                                    </select>
                                </div>
                                <div className="col-span-4">
                                    {r.action === "move_folder" ? (
                                        <input value={r.target_folder || ""} onChange={(e) => setRule(i, "target_folder", e.target.value)} placeholder={t("Ordner-Name", "Folder name")} className="w-full text-xs h-7 border border-neutral-200 rounded px-2" data-testid={`ms-rule-folder-${r.category}`} />
                                    ) : (
                                        <input value={r.notes || ""} onChange={(e) => setRule(i, "notes", e.target.value)} placeholder={t("Hinweis für KI", "AI hint")} className="w-full text-xs h-7 border border-neutral-200 rounded px-2" data-testid={`ms-rule-notes-${r.category}`} />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <Button onClick={saveRules} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="ms-rules-save">
                        <Save className="w-3.5 h-3.5 mr-1.5" /> {t("Regeln speichern", "Save rules")}
                    </Button>
                </div>
            )}
        </section>
    );
}

const CAT_LABEL = {
    task:     { de: "Wichtige Mails → Task", en: "Important → Task" },
    ad:       { de: "Werbung / Newsletter",  en: "Ads / Newsletters" },
    ticket:   { de: "Support-Ticket",        en: "Support ticket" },
    personal: { de: "Persönlich",            en: "Personal" },
    spam:     { de: "Spam",                  en: "Spam" },
};
